import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import ScratchCardCanvas from '../components/ScratchCardCanvas';
import { scratchService, campaignService } from '../services/api.service';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import PrizesModal from '../components/PrizesModal';
import Swal from 'sweetalert2';
import confetti from 'canvas-confetti';
import MyPrizesModal from '../components/MyPrizesModal';
import ProfileModal from '../components/ProfileModal';
import { toast } from 'react-hot-toast';

const GlobalStyle = createGlobalStyle`
  .prize-modal-popup {
    background: linear-gradient(135deg, 
      rgba(88, 28, 135, 0.95) 0%,
      rgba(67, 20, 102, 0.95) 100%
    ) !important;
    border: 1px solid rgba(255, 215, 0, 0.2) !important;
    border-radius: 20px !important;
    padding: 2rem !important;
    color: #ffd700 !important;
    box-shadow: 0 8px 32px rgba(76, 29, 149, 0.5) !important;
    backdrop-filter: blur(10px) !important;
  }

  .prize-modal-content {
    margin: 1.0rem 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }

  .prize-icon {
    font-size: 3rem;
    margin-bottom: 0rem;
  }

  .prize-text {
    font-size: 1.5rem;
    color: #ffd700;
    font-weight: 600;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  .prize-subtitle {
    font-size: 1.1rem;
    color: rgba(255, 215, 0, 0.8);
  }

  .prize-code {
    font-family: monospace;
    font-size: 1.3rem;
    color: #ffd700;
    background: rgba(255, 215, 0, 0.1);
    padding: 1rem 2rem;
    border-radius: 12px;
    margin: 1rem 0;
    letter-spacing: 3px;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 215, 0, 0.2);
  }

  .prize-modal-button {
    background: rgba(255, 215, 0, 0.1) !important;
    color: #ffd700 !important;
    border: 1px solid rgba(255, 215, 0, 0.2) !important;
    padding: 1rem 2rem !important;
    font-size: 1.1rem !important;
    font-weight: 500 !important;
    border-radius: 12px !important;
    transition: all 0.3s ease !important;

    &:hover {
      background: rgba(255, 215, 0, 0.2) !important;
      transform: translateY(-2px) !important;
    }

    &:focus {
      box-shadow: none !important;
    }
  }

  .whatsapp-button {
    background: linear-gradient(135deg, #25D366 0%, #128C7E 100%) !important;
    color: white !important;
    border: none !important;
    padding: 1rem 2rem !important;
    font-size: 1.1rem !important;
    font-weight: 600 !important;
    border-radius: 12px !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 0.8rem !important;
  margin:20px;
    &::before {
      content: '📱' !important;
      font-size: 1.2rem !important;
    }

    &:hover {
      background: linear-gradient(135deg, #128C7E 0%, #075E54 100%) !important;
      transform: translateY(-2px) !important;
      box-shadow: 0 6px 16px rgba(37, 211, 102, 0.4) !important;
    }

    &:focus {
      box-shadow: none !important;
    }
  }

  .subscription-modal {
    .swal2-html-container {
      margin-top: 1.5rem;
      font-size: 1.1rem;
      color: rgba(255, 215, 0, 0.9);
    }

    .whatsapp-button {
      background: linear-gradient(135deg, #25D366 0%, #128C7E 100%) !important;
      &::before {
        content: '📱' !important;
      }
    }
  }
`;

const ScratchCard = () => {
  const [campaign, setCampaign] = useState(null);
  const [participation, setParticipation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isRevealed, setIsRevealed] = useState(false);
  const scratchRef = useRef(null);
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { slug, testUrl } = useParams();
  const [isMyPrizesModalOpen, setIsMyPrizesModalOpen] = useState(false);
  const [myPrizes, setMyPrizes] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isParticipationStarted, setIsParticipationStarted] = useState(false);
  const urlParam = testUrl || slug;

  useEffect(() => {
    let mounted = true;

    const initializeCampaign = async () => {
      if (!urlParam || isParticipationStarted) {
        return;
      }

      try {
        setIsLoading(true);

        const campaignData = await campaignService.getCampaignBySlug(urlParam);
        if (!mounted) return;
        
        if (!campaignData) {
          setError('Campaña no encontrada');
          return;
        }

        setCampaign(campaignData);

        // Iniciar participación
        try {
          const response = await scratchService.startParticipation(campaignData._id);
          
          if (!mounted) return;

          // Verificar si la respuesta contiene un error
          if (!response.success && response.error) {
            if (response.error === 'phone_required') {
              Swal.fire({
                title: 'Necesitas un número de teléfono',
                text: 'Por favor, actualiza tu perfil con tu número de teléfono para participar',
                icon: 'warning',
                confirmButtonColor: '#7c3aed'
              });
              return;
            }

            if (response.error === 'not_subscribed') {
              Swal.fire({
                title: 'Suscríbete a Pamplonews',
                html: `
                  <div style="margin: 1rem 0;">
                    Para participar necesitas estar suscrito a Pamplonews.
                  </div>
                `,
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Suscribirme a Pamplonews',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#25D366',
                cancelButtonColor: '#6B7280',
                customClass: {
                  popup: 'prize-modal-popup',
                  confirmButton: 'whatsapp-button',
                  cancelButton: 'prize-modal-button'
                }
              }).then((result) => {
                if (result.isConfirmed) {
                  window.open('https://pmpl.news/dA9', '_blank');
                }
              });
              return;
            }

            // Manejar otros errores
            toast.error(response.message || 'Error al iniciar participación');
            return;
          }

          setParticipation(response.participation);
          setIsRevealed(response.alreadyParticipated);
          setIsParticipationStarted(true);

          if (response.participation.completed) {
            Swal.fire({
              title: 'Ya has participado hoy',
              text: 'Este es el premio que obtuviste',
              icon: 'info',
              confirmButtonColor: '#7c3aed'
            });
          }
        } catch (error) {
          console.error('Error starting participation:', error);
          
          // Si el error viene del servidor con un formato específico
          if (error.response?.data) {
            const { error: errorType, message } = error.response.data;
            
            if (errorType === 'not_subscribed') {
              Swal.fire({
                title: 'Suscríbete a Pamplonews',
                html: `
                  <div style="margin: 1rem 0;">
                    Para participar necesitas estar suscrito a Pamplonews.
                  </div>
                `,
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Suscríbete a Pamplonews',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#25D366',
                cancelButtonColor: '#6B7280',
                customClass: {
                  popup: 'prize-modal-popup',
                  confirmButton: 'whatsapp-button',
                  cancelButton: 'prize-modal-button'
                }
              }).then((result) => {
                if (result.isConfirmed) {
                  window.open('https://pmpl.news/dA9', '_blank');
                }
              });
              return;
            }
            
            toast.error(message || 'Error al iniciar participación');
          } else {
            toast.error('Error al iniciar participación. Por favor, inténtalo de nuevo.');
          }
        }
      } catch (err) {
        console.error('Error initializing campaign:', err);
        if (mounted) {
          setError(err.response?.data?.message || 'Error al cargar la campaña');
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initializeCampaign();

    return () => {
      mounted = false;
    };
  }, [urlParam, isParticipationStarted]);

  const handleLogout = async () => {
    const result = await Swal.fire({
      title: '¿Cerrar sesión?',
      text: '¿Estás seguro que quieres salir?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#7c3aed',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, cerrar sesión',
      cancelButtonText: 'Cancelar',
      background: '#ffffff',
      borderRadius: '16px',
      customClass: {
        popup: 'swal-popup',
        title: 'swal-title',
        htmlContainer: 'swal-text',
        confirmButton: 'swal-confirm',
        cancelButton: 'swal-cancel'
      }
    });

    if (result.isConfirmed) {
      try {
        await logout();
        navigate('/login');
      } catch (error) {
        console.error('Error al cerrar sesión:', error);
        Swal.fire({
          title: 'Error',
          text: 'No se pudo cerrar la sesión',
          icon: 'error',
          confirmButtonColor: '#7c3aed'
        });
      }
    }
  };

  const shootGoldConfetti = () => {
    const end = Date.now() + 3000;
    const colors = ['#ffd700', '#ffb347', '#ffed4a'];

    const shoot = () => {
      // Calcular el ancho de la ventana y ajustar el origen x
      const windowWidth = window.innerWidth;
      const x = 0.5; // Esto debería estar bien, pero asegurémonos

      confetti({
        particleCount: 80,
        angle: 90,
        spread: 120, // Aumentado para mejor distribución
        origin: { 
          x: x,
          y: 0.4  // Ajustado un poco más abajo
        },
        colors: colors,
        shapes: ['circle', 'square'],
        gravity: 0.7, // Reducido para que caiga más lento
        scalar: 1.5,
        drift: 0,
        ticks: 200,
        zIndex: 9999,
        disableForReducedMotion: true // Accesibilidad
      });
    };

    // Disparo inicial
    shoot();

    // Disparos continuos
    const interval = setInterval(() => {
      if (Date.now() > end) {
        return clearInterval(interval);
      }
      shoot();
    }, 250); // Ajustado el intervalo para mejor efecto

    // Limpieza del intervalo si el componente se desmonta
    return () => clearInterval(interval);
  };

  const showLoseEffect = () => {
    confetti({
      particleCount: 50,
      spread: 120,
      origin: { 
        x: 0.5,
        y: 0.4 
      },
      colors: ['#808080', '#A9A9A9'],
      gravity: 1.2,
      scalar: 1,
      drift: 0,
      ticks: 100,
      zIndex: 9999,
      disableForReducedMotion: true
    });
  };

  const showPrizeModal = (prize) => {
    const isWinner = prize.name !== campaign.noWinMessage;

    console.log('Prize data:', prize); // Para debug

    const handleWhatsAppShare = () => {
      const phoneNumber = user?.userData?.phone?.replace(/[\s\-\(\)]/g, '') || '';
      
      if (!phoneNumber) {
        toast.error('No se encontró un número de teléfono válido');
        return;
      }

      // Verificar que tenemos el código del premio
      const prizeCode = prize.redeemCode || prize.code;
      if (!prizeCode) {
        toast.error('No se encontró el código del premio');
        return;
      }

      let formattedPhone = phoneNumber;
      if (!formattedPhone.startsWith('+')) {
        formattedPhone = formattedPhone.startsWith('34') ? formattedPhone : `34${formattedPhone}`;
      }
      formattedPhone = formattedPhone.replace('+', '');
      
      const message = encodeURIComponent(
        `🎉 ¡Has ganado un premio!\n\n` +
        `Premio: ${prize.name}\n` +
        `Código: ${prizeCode}\n\n` +
        `Instrucciones de canjeo:\n${campaign?.redeemInstructions || 'Contacta con el establecimiento'}`
      );

      const whatsappUrl = `https://wa.me/${formattedPhone}?text=${message}`;
      window.open(whatsappUrl, '_blank');
    };

    // Añadir el código del premio al HTML si está disponible
    const prizeCode = prize.redeemCode || prize.code;
    const prizeHtml = `
      <div class="prize-modal-content">
        <div class="prize-text">${prize.name}</div>
        ${isWinner ? `
          <div class="prize-code">Código: ${prizeCode}</div>
          <div class="redeem-instructions">
            <h3 style="margin-top: 20px; font-size: 1.1em; color: #ffd700;">Instrucciones de canjeo:</h3>
            <p style="margin-top: 10px; font-size: 0.9em; line-height: 1.4;">${campaign?.redeemInstructions || 'Contacta con el establecimiento'}</p>
          </div>
        ` : ''}
      </div>
    `;

    Swal.fire({
      title: isWinner ? '¡Felicidades!' : 'Lo sentimos',
      html: prizeHtml,
      showConfirmButton: true,
      confirmButtonText: isWinner ? '¡Genial!' : 'Aceptar',
      showDenyButton: isWinner ? true : false, // Aseguramos que solo se muestra si es ganador
      denyButtonText: isWinner ? 'Guardar en WhatsApp' : undefined,
      customClass: {
        popup: 'prize-modal-popup',
        confirmButton: 'prize-modal-button',
        denyButton: isWinner ? 'whatsapp-button' : undefined
      },
      buttonsStyling: false,
      background: 'transparent',
      backdrop: `
        rgba(0,0,0,0.8)
        url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M50 50c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10s-10-4.477-10-10 4.477-10 10-10zM10 10c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10S0 25.523 0 20s4.477-10 10-10zm10 8c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8zm40 40c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
      `
    }).then((result) => {
      if (result.isDenied) {
        handleWhatsAppShare();
      }
    });
  };

  const handleScratchComplete = async (percentageRevealed) => {
    if (percentageRevealed > 70 && !isRevealed && !participation.alreadyParticipated) {
      setIsRevealed(true);
      try {
        await scratchService.completeParticipation(participation._id);
        showPrizeModal(participation.prize);
      } catch (err) {
        console.error('Error al completar la participación:', err);
      }
    }
  };

  const loadMyPrizes = async () => {
    try {
      const prizes = await scratchService.getMyPrizes();
      setMyPrizes(prizes || []);
    } catch (error) {
      console.error('Error al cargar premios:', error);
      setMyPrizes([]);
    }
  };

  const handleMyPrizesClick = () => {
    loadMyPrizes();
    setIsMyPrizesModalOpen(true);
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <>
      <GlobalStyle />
      <Container>
        <GradientTopBar>
          <BackButton onClick={() => navigate('/')}>
            <BackIcon>🏠</BackIcon>
            <BackText>Inicio</BackText>
          </BackButton>
          <Title>
            <MainTitle>El Rasca y Gana</MainTitle>
            <SubTitle>de Pamplonews</SubTitle>
          </Title>
          <UserMenuContainer>
            <MenuButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <UserAvatar>
                {user?.email?.charAt(0).toUpperCase() || '👤'}
              </UserAvatar>
            </MenuButton>
            {isMenuOpen && (
              <MenuDropdown>
                <MenuItem onClick={() => {
                  setIsMenuOpen(false);
                  setIsProfileModalOpen(true);
                }}>
                  <MenuIcon>👤</MenuIcon>
                  <span>Mi Perfil</span>
                </MenuItem>
                <MenuItem onClick={() => {
                  setIsMenuOpen(false);
                  handleMyPrizesClick();
                }}>
                  <MenuIcon>🏆</MenuIcon>
                  <span>Mis Premios</span>
                </MenuItem>
                <MenuDivider />
                <MenuItem onClick={() => {
                  setIsMenuOpen(false);
                  handleLogout();
                }}>
                  <MenuIcon>↪</MenuIcon>
                  <span>Cerrar Sesión</span>
                </MenuItem>
              </MenuDropdown>
            )}
          </UserMenuContainer>
        </GradientTopBar>

        {error ? (
          <ErrorMessage>
            {error}
            <ButtonsContainer>
              <PromotionalButton 
                href="https://tally.so/r/nGb6pe" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                ¿Quieres promocionar tu negocio?
              </PromotionalButton>
            </ButtonsContainer>
          </ErrorMessage>
        ) : (
          <>
            {campaign?.logo && (
              <LogoContainer>
                <CampaignLogo 
                  src={campaign.logo} 
                  alt={`Logo de ${campaign.name}`}
                />
              </LogoContainer>
            )}
            {campaign && (
              <CompactCampaignInfo>
                <CampaignMeta>
                  <div>
                    <CampaignTitle>{campaign.name}</CampaignTitle>
                  </div>
                </CampaignMeta>
              </CompactCampaignInfo>
            )}

            <MainContent>
              <CardWrapper>
                <CardContainer>
                  {participation && (
                    <ScratchCardCanvas
                      ref={scratchRef}
                      width={300}
                      height={100}
                      brushSize={25}
                      onComplete={handleScratchComplete}
                      prize={participation.prize.name}
                    />
                  )}
                </CardContainer>
                <CardGlow />
              </CardWrapper>

              <Instructions>
                Rasca la tarjeta para descubrir tu premio
              </Instructions>
            </MainContent>

            <ButtonsContainer>
              <StyledInfoButton onClick={() => setIsModalOpen(true)}>
                Ver premios disponibles
              </StyledInfoButton>
              <PromotionalButton 
                href="https://tally.so/r/nGb6pe" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                ¿Quieres promocionar tu negocio?
              </PromotionalButton>
            </ButtonsContainer>

            {campaign && (
              <PrizesModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                campaign={campaign}
              />
            )}
          </>
        )}

        {/* Modales siempre disponibles */}
        <MyPrizesModal
          isOpen={isMyPrizesModalOpen}
          onClose={() => setIsMyPrizesModalOpen(false)}
          prizes={myPrizes}
          user={user}
        />

        <ProfileModal
          isOpen={isProfileModalOpen}
          onClose={() => setIsProfileModalOpen(false)}
          user={user}
        />
      </Container>
    </>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: url('/images/sort.jpeg') no-repeat center center fixed;
  background-size: cover;
  padding: 0;
  gap: 0.5rem;
  position: relative;
  overflow: hidden;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.1); // Overlay oscuro para mejorar legibilidad
    z-index: -1;
  }
`;

const GradientTopBar = styled.header`
  position: relative;
  padding-top: 0.8rem;
  padding-bottom: 0.8rem;
  margin-bottom: 0.5rem;
`;

const Title = styled.div`
  text-align: center;
  margin-top: 1.9rem;
`;

const MainTitle = styled.h1`
  font-family: 'Fredoka One', sans-serif;
  font-size: 2.1rem;
  font-weight: 700;
  color: #ffd700;
  letter-spacing: 1px;
  -webkit-text-stroke: 3px white;
  margin: 0;
  margin-top: 1.9rem;
  text-shadow: 
    0 2px 4px rgba(0, 0, 0, 0.3),
    0 0 10px rgba(255, 215, 0, 0.5);
`;

const SubTitle = styled.h2`
  font-family: 'Fredoka One', sans-serif;
  font-size: 1.3rem;
  font-weight: 500;
  color: #ffd700;
  letter-spacing: 1px;
  -webkit-text-stroke: 1px white;
  margin: 0;
  text-shadow: 
    0 2px 4px rgba(0, 0, 0, 0.3),
    0 0 10px rgba(255, 215, 0, 0.5);
`;

const UserMenuContainer = styled.div`
  position: absolute;
  right: 1rem;
  top: 0.8rem;
  z-index: 9999;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 215, 0, 0.1);
  border: 2px solid rgba(255, 215, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffd700;
  font-size: 1.2rem;
  font-weight: 500;
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);

  &:hover {
    background: rgba(255, 215, 0, 0.2);
    border-color: rgba(255, 215, 0, 0.3);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }
`;

const MenuDropdown = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background: rgba(88, 28, 135, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 215, 0, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  padding: 0.5rem;
  min-width: 200px;
  z-index: 9999;
  animation: slideIn 0.2s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 0.8rem 1rem;
  background: none;
  border: none;
  border-radius: 8px;
  color: rgba(255, 215, 0, 0.8);
  display: flex;
  align-items: center;
  gap: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  text-align: left;

  &:hover {
    background: rgba(255, 215, 0, 0.1);
    color: #ffd700;
  }
`;

const MenuIcon = styled.span`
  font-size: 1.2rem;
  width: 24px;
  text-align: center;
`;

const MenuDivider = styled.div`
  height: 1px;
  background: rgba(255, 215, 0, 0.1);
  margin: 0.5rem 0;
`;

const CompactCampaignInfo = styled.div`
  padding: 0.8rem;
  text-align: center;
  position: relative;
  z-index: 1;
  margin-top: 0rem;
`;

const CampaignMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
`;

const CampaignTitle = styled.h2`
  font-family: 'Fredoka One', sans-serif;
  color: #ffd700;
  font-size: 1.4rem;
  margin: 0;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-align: center;
`;

const CampaignDescription = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  margin: 0.4rem 0;
  line-height: 1.4;
  letter-spacing: 0.3px;
  font-weight: 400;
  text-align: center;
  max-width: 600px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 1rem;
  gap: 0.5rem;
  position: relative;
  z-index: 1;
`;

const CardWrapper = styled.div`
  position: relative;
  margin: 0.5rem 0;
  width: 300px;
  height: 100px;
  
  &::before {
    content: '';
    position: absolute;
    inset: -1px;
    z-index: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.4) 0%,
      rgba(255, 255, 255, 0.1) 50%,
      rgba(255, 255, 255, 0.4) 100%
    );
    filter: blur(2px);
    box-shadow: 
      inset 0 0 10px rgba(255, 255, 255, 0.5),
      0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const CardContainer = styled.div`
  width: 300px;
  height: 100px;
  overflow: hidden;
  background: linear-gradient(145deg, #e6e6e6, #cccccc);
  position: relative;
  z-index: 1;
  box-shadow:
    inset 0 2px 5px rgba(255, 255, 255, 0.5),
    inset 0 -2px 5px rgba(0, 0, 0, 0.1);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    background: radial-gradient(
      circle at 30% 20%,
      rgba(255, 255, 255, 0.2) 0%,
      transparent 50%
    );
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    background: linear-gradient(
      135deg,
      transparent 0%,
      rgba(255, 255, 255, 0.1) 50%,
      transparent 100%
    );
  }
`;

const CardGlow = styled.div`
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;

  z-index: 0;

`;

const Instructions = styled.p`
  font-family: 'Roboto', sans-serif;
  color: white;
  text-align: center;
  margin: 0.5rem 0;
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const PrizeReveal = styled.div`
  text-align: center;
  margin-top: 1rem;
  padding: 1.5rem 2rem;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 215, 0, 0.2);
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, 
      rgba(255, 215, 0, 0.1) 0%, 
      rgba(255, 179, 71, 0.1) 100%
    );
    opacity: 0.5;
  }

  h3 {
    color: #ffd700;
    margin: 0.5rem 0;
    font-size: 1.3rem;
    font-weight: 600;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
    letter-spacing: 0.5px;
  }

  p {
    color: #ffb347;
    font-size: 1.4rem;
    font-weight: bold;
    margin: 0;
    text-shadow: 0 2px 8px rgba(255, 215, 0, 0.4);
    letter-spacing: 0.5px;
  }
`;

const PrizeIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

const ButtonsContainer = styled.div`
  position: fixed;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 1.2rem;
  z-index: 10;
  width: auto;
  justify-content: center;
  padding: 0 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 90%;
  }
`;

const StyledInfoButton = styled.button`
  background: rgba(255, 215, 0, 0.15);
  border: 1px solid rgba(255, 215, 0, 0.3);
  color: #ffd700;
  padding: 0.5rem 1.2rem;
  border-radius: 16px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
  letter-spacing: 0.5px;
  font-weight: 500;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 215, 0, 0.25);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const PromotionalButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  background: linear-gradient(135deg, #ffd700 0%, #ffb347 100%);
  color: #581C87;
  text-decoration: none;
  border: none;
  padding: 0.5rem 1.2rem;
  border-radius: 16px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);
  white-space: nowrap;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.2);

  &:hover {
    background: linear-gradient(135deg, #ffdb1c 0%, #ffc067 100%);
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 6px 20px rgba(255, 215, 0, 0.4);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);
  }
`;

const StoreIcon = styled.span`
  font-size: 1.2rem;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  margin: 2rem;
  font-weight: 500;
`;

const BackButton = styled.button`
  position: absolute;
  left: 1rem;
  top: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 215, 0, 0.1);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  color: #ffd700;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 9999;

  &:hover {
    background: rgba(255, 215, 0, 0.2);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 215, 0, 0.1);
  }
`;

const BackIcon = styled.span`
  font-size: 1.2rem;
`;

const BackText = styled.span`
  font-size: 0.9rem;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  margin: 0rem auto;
`;

const CampaignLogo = styled.img`
  max-width: 100%;
  max-height: 100px;
  object-fit: contain;
`;

const CampaignName = styled.h3`
  color: #ffd700;
  font-size: 1.4rem;
  margin: 0.5rem 0 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  font-weight: 500;
`;

export default ScratchCard;